import * as Sentry from "@sentry/react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export interface PortalUser {
  id: string;
  email: string;
  /**
   * SIM-1816: optional display name. Not yet provided by the session endpoint
   * (see SIM-1830 to plumb the Google OAuth name through); when present the
   * account menu shows it above the email and the avatar uses its initials.
   */
  name?: string | null;
}

interface AuthContextType {
  user: PortalUser | null;
  loading: boolean;
  /** `returnTo` is the portal path to land on after sign-in; the platform validates it. */
  signInWithGoogle: (returnTo?: string | null) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** SIM-2830: employees are identified by email domain, client-side only. */
const INTERNAL_EMAIL_DOMAIN = "@simplicityed.com";

function isInternalEmail(email: string | null | undefined): boolean {
  return typeof email === "string" && email.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN);
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/portal/auth/session", { credentials: "include" })
      .then((res) => {
        if (!res.ok) return null;
        return res.json() as Promise<{ userId: string; email: string; name?: string | null }>;
      })
      .then((data) => {
        setUser(data ? { id: data.userId, email: data.email, name: data.name ?? null } : null);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // SIM-2830: without this every Sentry issue reads "Users: 0", which looks like
  // "nobody affected" rather than "no user context set". Deliberately sends the
  // portal user ID and a derived internal/external boolean ONLY — the email
  // address and display name must never leave the browser.
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id });
      Sentry.setTag("spark_internal_user", isInternalEmail(user.email) ? "true" : "false");
    } else {
      Sentry.setUser(null);
      Sentry.setTag("spark_internal_user", undefined);
    }
  }, [user]);

  const signInWithGoogle = (returnTo?: string | null) => {
    const query = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
    window.location.href = `/portal/auth/google/login${query}`;
  };

  const signOut = async () => {
    await fetch("/portal/auth/signout", { method: "POST", credentials: "include" });
    setUser(null);
    window.location.href = "/portal/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

/**
 * SIM-2830: non-throwing probe for "is the signed-in user a Simplicity
 * employee", derived from the email DOMAIN locally so query-layer hooks can tag
 * a Sentry alert without needing an AuthProvider in the tree (and without ever
 * handling the address itself). Returns false when unauthenticated.
 */
export function useIsInternalUser(): boolean {
  const context = useContext(AuthContext);
  return isInternalEmail(context?.user?.email);
}
